import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseTimerResult {
  timeRemaining: number;
  elapsedMs: number;
  isRunning: boolean;
  start: () => void;
  pause: () => void;
  reset: (seconds: number) => void;
  getElapsedMs: () => number;
}

export function useTimer(
  initialSeconds: number,
  onTimeout?: () => void
): UseTimerResult {
  const [timeRemaining, setTimeRemaining] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const startTimeRef = useRef<number>(0);
  const elapsedRef = useRef<number>(0);

  useEffect(() => {
    if (!isRunning || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsRunning(false);
          onTimeout?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, timeRemaining, onTimeout]);

  const start = useCallback(() => {
    startTimeRef.current = Date.now();
    setIsRunning(true);
  }, []);

  const pause = useCallback(() => {
    if (isRunning) {
      elapsedRef.current += Date.now() - startTimeRef.current;
    }
    setIsRunning(false);
  }, [isRunning]);

  const reset = useCallback((seconds: number) => {
    setTimeRemaining(seconds);
    setIsRunning(false);
    startTimeRef.current = 0;
    elapsedRef.current = 0;
  }, []);

  const getElapsedMs = useCallback(() => {
    if (isRunning) {
      return elapsedRef.current + (Date.now() - startTimeRef.current);
    }
    return elapsedRef.current;
  }, [isRunning]);

  return {
    timeRemaining,
    elapsedMs: elapsedRef.current,
    isRunning,
    start,
    pause,
    reset,
    getElapsedMs,
  };
}
